
import Vue from 'vue'
import { getCallbotReportSummary } from '@/api/callbot'
import { CallbotReportConversionKey, CallbotReportPeriodDays, CallbotReportSummary } from '@/types/callbot'
import { callbotConversionWidth, callbotDonutBackground } from './callbotReport'

type KpiTone = 'tone-indigo' | 'tone-teal' | 'tone-wasabi' | 'tone-ink'

const CONVERSION_META: Record<CallbotReportConversionKey, { label: string; tone: '' | 'teal' | 'accent' }> = {
    calls: { label: 'Все звонки', tone: '' },
    reached: { label: 'Дозвонились', tone: '' },
    pressed1: { label: 'Нажали 5', tone: 'teal' },
    leadsInWork: { label: 'Тёплые лиды', tone: 'accent' },
    recorded: { label: 'Записаны', tone: 'teal' },
}

export default Vue.extend({
    name: 'CallbotAnalytics',
    props: {
        projectId: { type: String, required: true },
        hasActiveRuns: { type: Boolean, default: false },
    },
    data() {
        return {
            report: null as CallbotReportSummary | null,
            periodDays: 30 as CallbotReportPeriodDays,
            runId: '',
            loading: false,
            error: '',
            requestId: 0,
            refreshTimer: null as number | null,
        }
    },
    computed: {
        campaigns(): CallbotReportSummary['campaigns'] {
            return this.report?.campaigns || []
        },
        kpis(): Array<{ label: string; value: string; note: string; tone: KpiTone }> {
            if (!this.report) return []
            return [
                {
                    label: 'Всего звонков',
                    value: this.formatNumber(this.report.totals.calls),
                    note: this.changeLabel(this.report.changes.callsPct),
                    tone: 'tone-indigo',
                },
                {
                    label: 'Дозвонились',
                    value: this.formatNumber(this.report.totals.reached),
                    note: this.formatPercent(this.report.rates.reachedOfCalls),
                    tone: 'tone-teal',
                },
                {
                    label: 'Нажали 5',
                    value: this.formatNumber(this.report.totals.pressed1),
                    note: `${this.formatPercent(this.report.rates.pressed1OfReached)} от дозвонов`,
                    tone: 'tone-wasabi',
                },
                {
                    label: 'Лиды в работе',
                    value: this.formatNumber(this.report.totals.leadsInWork),
                    note: `${this.formatPercent(this.report.rates.leadsInWorkOfCalls)} от всех`,
                    tone: 'tone-indigo',
                },
                {
                    label: 'Записаны',
                    value: this.formatNumber(this.report.totals.recorded),
                    note: `${this.formatPercent(this.report.rates.recordedOfLeads)} от лидов`,
                    tone: 'tone-teal',
                },
                {
                    label: 'Стоимость лида',
                    value:
                        this.report.totals.costPerLeadRub === null
                            ? '—'
                            : this.formatMoney(this.report.totals.costPerLeadRub),
                    note: this.changeLabel(this.report.changes.costPerLeadPct),
                    tone: 'tone-ink',
                },
            ]
        },
        outcomes(): Array<{ label: string; value: number; tone: string }> {
            if (!this.report) return []
            return [
                { label: 'Дозвонились', value: this.report.outcomes.reached, tone: 'indigo' },
                {
                    label: 'Не дозвонились',
                    value: this.report.outcomes.notReached,
                    tone: 'neutral',
                },
                {
                    label: 'Ошибка телефонии',
                    value: this.report.outcomes.telephonyErrors,
                    tone: 'red',
                },
            ]
        },
        conversion(): Array<{
            key: CallbotReportConversionKey
            label: string
            value: number
            percentOfCalls: number
            width: string
            tone: string
        }> {
            if (!this.report) return []
            return this.report.conversion.map(item => ({
                ...item,
                ...CONVERSION_META[item.key],
                width: callbotConversionWidth(item.value, item.percentOfCalls),
            }))
        },
        donutBackground(): string {
            return this.report ? callbotDonutBackground(this.report) : '#E9ECF4'
        },
    },
    watch: {
        projectId() {
            this.report = null
            this.runId = ''
            void this.loadReport()
        },
    },
    mounted() {
        void this.loadReport()
        this.refreshTimer = window.setInterval(() => {
            if (this.hasActiveRuns && !this.loading) void this.loadReport()
        }, 30_000)
    },
    beforeDestroy() {
        if (this.refreshTimer !== null) window.clearInterval(this.refreshTimer)
        this.requestId += 1
    },
    methods: {
        async loadReport() {
            const requestId = ++this.requestId
            this.loading = true
            this.error = ''
            try {
                const result = await getCallbotReportSummary(
                    this.projectId,
                    this.periodDays,
                    this.runId || undefined
                )
                if (requestId !== this.requestId) return
                if (result.error || !result.data) {
                    this.error = result.error?.message || 'Не удалось загрузить аналитику'
                    return
                }
                this.report = result.data
            } catch (error) {
                if (requestId !== this.requestId) return
                this.error = error instanceof Error ? error.message : 'Не удалось загрузить аналитику'
            } finally {
                if (requestId === this.requestId) this.loading = false
            }
        },
        formatNumber(value: number) {
            return new Intl.NumberFormat('ru-RU').format(value)
        },
        formatMoney(value: number) {
            return `${new Intl.NumberFormat('ru-RU', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
            }).format(value)} ₽`
        },
        formatPercent(value: number) {
            return `${new Intl.NumberFormat('ru-RU', {
                minimumFractionDigits: value % 1 === 0 ? 0 : 1,
                maximumFractionDigits: 1,
            }).format(value)}%`
        },
        changeLabel(value: number | null) {
            if (value === null) return 'Нет данных для сравнения'
            if (value === 0) return 'Без изменений к прошлому периоду'
            const sign = value > 0 ? '+' : '−'
            return `${sign}${this.formatPercent(Math.abs(value))} к прошлому периоду`
        },
    },
})
